import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PartnerTierListType, ChartTrendType, TrendChartKey, PerformanceTrendsStateType, ConsentStatusType } from "./Types";
import { updatePartnerTierListPayloadType } from "../../saga/partner-program/saga-actions";

const defaultChartTrendState: ChartTrendType = {
    labels: [],
    values: [],
    loading: false,
    error: null,
    selectedPeriod: "1year",
};

type initialStateType = {
    fetchedPartnerTierID: number;
    partnerTierList: PartnerTierListType;
    performanceTrends: PerformanceTrendsStateType;
    consentStatus: ConsentStatusType | null;
    consentLoading: boolean;
    /** true once the consent-status request for the current partner has finished */
    consentChecked: boolean;
    /** false = API reported partner not eligible (toggle stays off); null = unknown */
    partnerProgramConsentEligible: boolean | null;
    consentError: string | null;
    setConsentLoading: boolean;
    status: {
        partnerTierListLoading: boolean;
        addPartnerTierLoading: boolean;
        addPartnerTierStatus: boolean;
        addPartnerTierMessage: string;
        updatePartnerTierStatus: boolean;
        updatePartnerTierMessage: string,
        updatePartnerTierLoading: boolean,
        deletePartnerTierStatus: boolean;
        deletePartnerTierMessage: string;
        deletePartnerTierLoading: boolean;
    }
}

var initialState: initialStateType = {
    fetchedPartnerTierID: 0,
    partnerTierList: [],
    performanceTrends: {
        tierStatus: { ...defaultChartTrendState },
        totalPoints: { ...defaultChartTrendState },
        netNewLicenses: { ...defaultChartTrendState },
        totalLicenses: { ...defaultChartTrendState },
    },
    consentStatus: null,
    consentLoading: false,
    consentChecked: false,
    partnerProgramConsentEligible: null,
    consentError: null,
    setConsentLoading: false,
    status: {
        partnerTierListLoading: false,
        addPartnerTierLoading: false,
        addPartnerTierStatus: false,
        addPartnerTierMessage: "",
        updatePartnerTierStatus: false,
        updatePartnerTierMessage: "",
        updatePartnerTierLoading: false,
        deletePartnerTierStatus: false,
        deletePartnerTierMessage: "",
        deletePartnerTierLoading: false,
    }
}
const partnerTierSlice = createSlice({
    name: "partner_program",
    initialState: initialState,
    reducers: {
        setFetchedPartnerTierID: (state, action) => {
            state.fetchedPartnerTierID = action.payload;
        },
        setpartnerTierListLoading: (state, action) => {
            state.status.partnerTierListLoading = action.payload; 
        },
        setAddPartnerTierLoading: (state, action) => {
            state.status.addPartnerTierLoading = action.payload; 
        },
        setPartnerTierList: (state, action) => {
            state.partnerTierList = action.payload;
        },
        setAddPartnerTierStatus: (state, action) => {
            state.status.addPartnerTierStatus = action.payload; 
        },
        setAddPartnerTierMessage: (state, action) => {
            state.status.addPartnerTierMessage = action.payload; 
        },
        setUpdatePartnerTierStatus: (state, action) => {
            state.status.updatePartnerTierStatus = action.payload; 
        },
        setUpdatePartnerTierMessage: (state, action) => {
            state.status.updatePartnerTierMessage = action.payload; 
        },
         setUpdatePartnerTierLoading : (state, action) => {
            state.status.updatePartnerTierLoading = action.payload;
        },
        setPartnerTierListUpdate: (
      state,
      action: PayloadAction<updatePartnerTierListPayloadType>
    ) => {
      const updated = action.payload;
      const idx = state.partnerTierList.findIndex((t) => t.ID === updated.ID);
      if (idx !== -1) {
        state.partnerTierList[idx] = {
          ...state.partnerTierList[idx],
          tier_name: updated.tier_name,
          min_points: Number(updated.min_points),
          max_points: Number(updated.max_points),
          short_description: updated.short_description,
          long_description: updated.long_description,
        };
      }
    },

        setDeletePartnerTierStatus: (state, action) => {
            state.status.deletePartnerTierStatus = action.payload; 
        },
        setDeletePartnerTierMessage: (state, action) => {
            state.status.deletePartnerTierMessage = action.payload; 
        },
        setDeletePartnerTierLoading: (state, action) => {
            state.status.deletePartnerTierLoading = action.payload; 
        },
        // Performance Trends reducers
        setTrendLoading: (state, action: PayloadAction<{ chartKey: TrendChartKey; loading: boolean }>) => {
            state.performanceTrends[action.payload.chartKey].loading = action.payload.loading;
        },
        setTrendError: (state, action: PayloadAction<{ chartKey: TrendChartKey; error: string | null }>) => {
            state.performanceTrends[action.payload.chartKey].error = action.payload.error;
        },
        setTrendData: (state, action: PayloadAction<{ chartKey: TrendChartKey; labels: string[]; values: (string | number)[] }>) => {
            state.performanceTrends[action.payload.chartKey].labels = action.payload.labels;
            state.performanceTrends[action.payload.chartKey].values = action.payload.values;
        },
        setTrendPeriod: (state, action: PayloadAction<{ chartKey: TrendChartKey; period: string }>) => {
            state.performanceTrends[action.payload.chartKey].selectedPeriod = action.payload.period;
        },
        // Consent reducers
        setConsentStatus: (state, action: PayloadAction<ConsentStatusType | null>) => {
            state.consentStatus = action.payload;
        },
        setConsentLoading: (state, action: PayloadAction<boolean>) => {
            state.consentLoading = action.payload;
        },
        setConsentChecked: (state, action: PayloadAction<boolean>) => {
            state.consentChecked = action.payload;
        },
        setConsentError: (state, action: PayloadAction<string | null>) => {
            state.consentError = action.payload;
        },
        setSetConsentLoading: (state, action: PayloadAction<boolean>) => {
            state.setConsentLoading = action.payload;
        },
        setPartnerProgramConsentEligible: (state, action: PayloadAction<boolean | null>) => {
            state.partnerProgramConsentEligible = action.payload;
        },
    }
});

export const partnerTierSliceReducer = partnerTierSlice.reducer;
export const partnerTierSliceAction = partnerTierSlice.actions;